import React from "react";
import { LOADING_FILL, DELIVERY_FILL, LOADING_INK, DELIVERY_INK, OUTLINE } from "../geotab/geotabFormat.js";

// Planned routes before and after a Dispatch correction, side by side: AVANT ➜ APRÈS.
const ADDED = "var(--color-accent)", REMOVED = "#c5524a";
const dayDiff = (a, b) => Math.round((Date.parse(`${a}T12:00:00Z`) - Date.parse(`${b}T12:00:00Z`)) / 86400000);
const clock = minutes => `${String(Math.floor(minutes / 60) % 24).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
const duration = minutes => `${minutes < 0 ? "−" : "+"}${Math.floor(Math.abs(minutes) / 60)} h ${String(Math.abs(minutes) % 60).padStart(2, "0")}`;

function Track({ segments, flagged, height, x, at }) {
  const flags = [...segments.filter(s => s.state).reduce((out, s) => out.set(s.text, [...(out.get(s.text) || []), s]), new Map()).values()];
  return <div className="relative" style={{ height, background: "repeating-linear-gradient(90deg,transparent 0 calc(var(--hour) - 1px),#eef2f6 calc(var(--hour) - 1px) var(--hour))" }}>
    {flags.map(group => {
      const left = x(Math.min(...group.map(s => at(s.start)))), right = x(Math.max(...group.map(s => at(s.end))));
      const first = group[0];
      return <div key={first.text} data-move={first.move || undefined} data-role={first.state === "added" ? "dst" : undefined}
        className="absolute h-[15px] whitespace-nowrap rounded-t-[4px] px-1.5 text-[10px] font-extrabold leading-[15px] tracking-wide text-white"
        style={{ left: `${left}%`, minWidth: `${right - left}%`, top: 1, background: first.state === "added" ? ADDED : REMOVED }}>{first.text}</div>;
    })}
    {segments.map(s => {
      const left = x(at(s.start)), removed = s.state === "removed";
      const fill = s.kind === "loading" ? [LOADING_FILL, LOADING_INK] : [DELIVERY_FILL, DELIVERY_INK];
      return <div key={s.key} title={`${s.label} ${clock(at(s.start))}–${clock(at(s.end))}`} data-move={removed ? s.move || undefined : undefined} data-role={removed ? "src" : undefined}
        className={`absolute h-[18px] overflow-hidden text-ellipsis whitespace-nowrap rounded-[2px] px-1 text-[10.5px] font-semibold leading-[16px] ${removed ? "line-through" : ""}`}
        style={{ left: `${left}%`, width: `${Math.max(x(at(s.end)) - left, 0.4)}%`, top: flagged ? 17 : 2,
          background: removed ? "repeating-linear-gradient(45deg,#fff 0 4px,#cbd5e1 4px 8px)" : fill[0], color: removed ? "#64748b" : fill[1],
          border: removed ? "1px dashed #475569" : s.state === "added" ? `2px solid ${ADDED}` : `1px solid ${OUTLINE}` }}>{s.label}</div>;
    })}
  </div>;
}

export default function DispatchChangeGantt({ rows }) {
  const box = React.useRef(null);
  const [arrows, setArrows] = React.useState([]);
  const [width, setWidth] = React.useState(0);
  const points = rows.flatMap(row => [...row.before, ...row.after].flatMap(s => [s.start, s.end]));
  const base = points.map(p => p.day).sort()[0];
  const at = point => point.minutes + 1440 * dayDiff(point.day, base);
  const start = Math.floor(Math.min(...points.map(at)) / 60) * 60;
  const end = Math.max(start + 360, Math.ceil(Math.max(...points.map(at)) / 60) * 60);
  // One label per hour when each column is wide enough, otherwise every two hours.
  const hours = (end - start) / 60, step = hours > 14 || (width && width < 1300) ? 2 : 1;
  const x = minutes => ((minutes - start) / (end - start)) * 100;
  const signature = JSON.stringify(rows.map(row => row.after.map(s => s.key)));
  // Dotted arrows follow the moved orders from the hatched stops in AVANT to their new route in APRÈS.
  React.useLayoutEffect(() => {
    const node = box.current;
    if (!node) return undefined;
    const draw = () => {
      const origin = node.getBoundingClientRect();
      setWidth(origin.width);
      setArrows([...node.querySelectorAll("[data-role=dst]")].flatMap(target => {
        const sources = [...node.querySelectorAll("[data-role=src]")].filter(s => s.dataset.move === target.dataset.move).map(s => s.getBoundingClientRect());
        if (!sources.length) return [];
        const x1 = (Math.min(...sources.map(r => r.left)) + Math.max(...sources.map(r => r.right))) / 2 - origin.left;
        const y1 = Math.max(...sources.map(r => r.bottom)) - origin.top;
        const flag = target.getBoundingClientRect(), x2 = flag.left + 22 - origin.left, y2 = flag.top - origin.top - 2;
        return [`M${x1},${y1} C${x1},${y1 + 22} ${x2},${y2 - 22} ${x2},${y2}`];
      }));
    };
    draw();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(draw) : null;
    observer?.observe(node);
    return () => observer?.disconnect();
  }, [signature]);
  const axis = <div className="relative h-[13px] text-[10px] leading-[13px] text-slate-500">
    {Array.from({ length: Math.floor(hours / step) + 1 }, (_, i) => start + i * step * 60).map(m => <span key={m} className={`absolute whitespace-nowrap ${m === end ? "-translate-x-full" : "-translate-x-1/2"}`} style={{ left: `${x(m)}%` }}>{clock(m)}</span>)}
  </div>;
  const span = { gridRow: `1 / ${rows.length + 3}` };
  return <div ref={box} role="group" aria-label="Tournées avant et après la correction du Dispatch" style={{ "--hour": `calc(100% / ${hours})` }}
    className="relative grid min-w-[56rem] grid-cols-[10rem_minmax(0,1fr)_1.5rem_minmax(0,1fr)] gap-x-1.5 gap-y-0.5 text-xs">
    <div className="rounded-[4px] border border-slate-400" style={{ ...span, gridColumn: 2 }} />
    <div className="flex items-center justify-center text-[22px] font-extrabold text-slate-900" style={{ ...span, gridColumn: 3 }} aria-hidden="true">➜</div>
    <div className="rounded-[4px] border-2 border-slate-900" style={{ ...span, gridColumn: 4 }} />
    {[["AVANT", "bg-slate-500", "Dispatch au début de votre saisie", 2], ["APRÈS", "bg-slate-900", "Dispatch corrigé", 4]].map(([tag, color, text, column]) =>
      <p key={tag} className="flex items-center gap-2 px-1.5 pt-0.5 text-[11px] leading-4 text-slate-700" style={{ gridRow: 1, gridColumn: column }}>
        <span className={`rounded-[4px] px-1.5 text-[10.5px] font-bold tracking-wide text-white ${color}`}>{tag}</span>{text}</p>)}
    <div className="px-[7px]" style={{ gridRow: 2, gridColumn: 2 }}>{axis}</div>
    <div className="px-[7px]" style={{ gridRow: 2, gridColumn: 4 }}>{axis}</div>
    {rows.map((row, index) => {
      const flagged = [...row.before, ...row.after].some(s => s.state);
      const height = flagged ? 37 : 22;
      const [endBefore, endAfter] = row.end.map(v => v && at(v));
      const cell = column => ({ gridRow: index + 3, gridColumn: column });
      const line = index ? "border-t border-slate-200" : "";
      return <React.Fragment key={row.key}>
        <div className="self-center leading-4" style={cell(1)}>
          <p className="font-bold text-slate-900">{row.name}</p>
          {row.role ? <p className="text-[11px] text-slate-500">{row.role}</p> : null}
          {endBefore != null && endAfter != null && endBefore !== endAfter ? <p className="whitespace-nowrap text-[11px] text-slate-700">Fin {clock(endBefore)} → <b>{clock(endAfter)}</b>{" "}
            <span className="rounded-[4px] bg-slate-900 px-1 font-bold text-white">{duration(endAfter - endBefore)}</span></p> : null}
        </div>
        <div className={`px-[7px] pb-0.5 ${line}`} style={cell(2)}><Track segments={row.before} flagged={flagged} height={height} x={x} at={at} /></div>
        <div className={`px-[7px] pb-0.5 ${line}`} style={cell(4)}><Track segments={row.after} flagged={flagged} height={height} x={x} at={at} /></div>
      </React.Fragment>;
    })}
    {arrows.length ? <svg className="pointer-events-none absolute inset-0 h-full w-full overflow-visible" aria-hidden="true">
      <defs><marker id="dispatch-move-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="#0f172a" /></marker></defs>
      {arrows.map(d => <path key={d} d={d} fill="none" stroke="#0f172a" strokeWidth="2" strokeDasharray="5 3" markerEnd="url(#dispatch-move-arrow)" />)}
    </svg> : null}
  </div>;
}
